import type { AppContext } from '@application/context/AppContext';
import { isRemoteMultiStationMode } from '@infra/licence/DataModeResolver';
import { getBusinessApiClient } from '@infra/api/BusinessApiClient';
import type { RemoteAuditLogRecord } from '@infra/api/BusinessApiClient';
import { logger } from '@infra/telemetry/Logger';
import { uuid } from '@shared/utils/uuid';
import type { AuditAction, AuditLogEntry } from '@shared/types/AuditLog';
import { AuditTrailService } from '@domain/rgpd/AuditTrail';

function mapAction(action: string): AuditAction {
  if (action.includes('create')) return 'create';
  if (action.includes('update')) return 'update';
  if (action.includes('delete')) return 'delete';
  if (action.includes('login')) return 'login';
  if (action.includes('logout')) return 'logout';
  if (action.includes('export')) return 'export';
  if (action.includes('import')) return 'import';
  return 'update';
}

function remoteToAuditEntry(remote: RemoteAuditLogRecord): AuditLogEntry {
  const utilisateur = remote.actor_name ?? remote.actor_email ?? 'Système';
  const entity = remote.resource_type;
  const description = `${remote.action} · ${entity}${remote.resource_id ? ` (${remote.resource_id.slice(0, 8)}…)` : ''}`;
  return {
    id: remote.id,
    tenant_id: remote.tenant_id,
    date: remote.created_at,
    utilisateur,
    action: mapAction(remote.action),
    entity,
    entity_id: remote.resource_id ?? undefined,
    description,
    level: 'info',
    payload: remote.metadata ?? undefined,
  };
}

/** Journal d'audit depuis la source adaptée au mode de données. */
export async function listTenantAuditLogs(
  ctx: AppContext,
  tenantId: string | undefined,
): Promise<AuditLogEntry[]> {
  if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
    const items = await getBusinessApiClient().listAuditLogs();
    return items.map(remoteToAuditEntry);
  }
  return ctx.localTables.list<AuditLogEntry>('audit_log', tenantId ?? undefined);
}

export async function recordAuditEvent(
  ctx: AppContext,
  input: {
    action: AuditAction;
    entity: string;
    entity_id?: string;
    description: string;
    payload?: Record<string, unknown>;
  },
): Promise<void> {
  const session = ctx.auth.getSession();
  const tenantId = session?.tenant_id ?? ctx.tenantContext.tryGetTenant()?.id ?? 'default';
  const entryBase: AuditLogEntry = {
    id: uuid('aud'),
    tenant_id: tenantId,
    date: new Date().toISOString(),
    utilisateur: session?.user_id ?? 'Système',
    action: input.action,
    entity: input.entity,
    entity_id: input.entity_id,
    description: input.description,
    level: 'info',
    payload: input.payload,
  };

  try {
    if (isRemoteMultiStationMode(ctx.getLockedDataMode())) {
      await getBusinessApiClient().createAuditLog({
        action: `${input.action}.${input.entity}`,
        resource_type: input.entity,
        resource_id: input.entity_id,
        metadata: { description: input.description, ...(input.payload ?? {}) },
      });
      return;
    }
    // A-09 : chaînage cryptographique
    const previous = (await ctx.localTables.list<AuditLogEntry>('audit_log', tenantId))
      .sort((a, b) => a.date.localeCompare(b.date))
      .at(-1);
    const hash = await AuditTrailService.hashEntry(entryBase, previous?.hash ?? '');
    await ctx.localTables.put('audit_log', { ...entryBase, hash });
  } catch (error) {
    logger.warn({ event: 'audit_write_failed', error: String(error), entity: input.entity });
  }
}
